import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { createAdminAccount } from '../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface AdminSetupForm {
  email: string;
  password: string;
  confirmPassword: string;
}

export function AdminSetup() {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<AdminSetupForm>();
  const password = watch('password');
  const navigate = useNavigate();
  const [adminExists, setAdminExists] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    try {
      const adminDoc = await getDoc(doc(db, 'admin', 'status'));
      setAdminExists(adminDoc.exists());
      setLoading(false);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setLoading(false);
    }
  };

  const onSubmit = async (data: AdminSetupForm) => {
    try {
      // Vérifier à nouveau si un admin existe
      const adminDoc = await getDoc(doc(db, 'admin', 'status'));
      if (adminDoc.exists()) {
        alert('Un compte administrateur existe déjà.');
        navigate('/login');
        return;
      }

      // Créer le compte admin
      await createAdminAccount(data.email, data.password);
      
      // Marquer qu'un admin existe
      await setDoc(doc(db, 'admin', 'status'), {
        exists: true,
        createdAt: new Date().toISOString()
      });

      alert('Compte administrateur créé avec succès ! Vous pouvez maintenant vous connecter.');
      navigate('/login');
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        alert('Un compte administrateur existe déjà.');
        navigate('/login');
      } else {
        alert('Erreur lors de la création du compte. Veuillez réessayer.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (adminExists) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
            Accès non autorisé
          </h2>
          <p className="text-gray-600 mb-4">
            Un compte administrateur existe déjà.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn"
          >
            Aller à la page de connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Création du compte administrateur
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Cette page ne peut être utilisée qu'une seule fois pour créer le compte admin initial
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                {...register('email', {
                  required: 'Email requis',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email invalide'
                  }
                })}
                type="email"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email administrateur"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Mot de passe</label>
              <input
                {...register('password', {
                  required: 'Mot de passe requis',
                  minLength: {
                    value: 8,
                    message: 'Le mot de passe doit contenir au moins 8 caractères'
                  }
                })}
                type="password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirmer le mot de passe</label>
              <input
                {...register('confirmPassword', {
                  required: 'Veuillez confirmer le mot de passe',
                  validate: value => value === password || 'Les mots de passe ne correspondent pas'
                })}
                type="password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirmer le mot de passe"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Créer le compte administrateur
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}